import {
  FlowType,
  InventoryFlow,
  SearchAdsSwitch,
  CampaignFormData,
  AdgroupFormData,
  AppProfilePageType,
  AdgroupConfig,
  Ios14Quota,
  CreateMode,
} from '@tt4b/ttam-enums';
import { computed, Ref, ref } from '@vue/composition-api';
import {
  useStdAdgroupIs,
  useStdObjectiveType,
  useAllowlist,
  useStdAdgroupAmazon,
  useAdgroupIos14,
  useAdgroupMode,
  NetStateEnum,
  useStore,
  useHooksCache,
  useCatalogStore,
} from '@tt4b/ttam-hooks';
import { i18n } from './../../../i18n';

import PlacementTTImage from '@tt4b/ttam-assets/src/images/placement/placement-tt.png';
import PlacementTTViewImage from '@tt4b/ttam-assets/src/images/placement/placement-tt-view.png';
import PlacementTTSearchAdsImage from '@tt4b/ttam-assets/src/images/placement/placement-tt-search-ads.png';
import PlacementTBImage from '@tt4b/ttam-assets/src/images/placement/placement-tb.png';
import PlacementTB1Image from '@tt4b/ttam-assets/src/images/placement/placement-tb-view1.png';
import PlacementTB2Image from '@tt4b/ttam-assets/src/images/placement/placement-tb-view2.png';
import PlacementTB3Image from '@tt4b/ttam-assets/src/images/placement/placement-tb-view3.png';

import PlacementPanpleImage from '@tt4b/ttam-assets/src/images/placement/placement-pangle.png';
import PlacementAdNetworkImage from '@tt4b/ttam-assets/src/images/placement/tiktok-ad-network-view.png';
import { InventoryFlowTypes } from './consts';

export function useInventoryFlowListPropsDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  const { adgroupConfig } = props;

  return {
    disabled: computed(() => adgroupConfig.value.disablePlacement),
  };
}

export function useFlowTypeOptionsPropsDisabled(props: {
  adgroupConfig: Ref<AdgroupConfig>;
  hasDisabledPlacement: Ref<boolean>;
}) {
  const { adgroupConfig, hasDisabledPlacement } = props;

  return {
    disabled: computed(() => adgroupConfig.value.disablePlacement || hasDisabledPlacement.value),
  };
}
export function useInventoryTypeListPropsList(isAutoFlowTypeDisabled: Ref<boolean>) {
  const getInventoryTypeDisabled = item => {
    if (item.value === FlowType.AUTO) {
      return isAutoFlowTypeDisabled.value;
    }
    return false;
  };
  const list = computed(() =>
    InventoryFlowTypes().map(o => {
      const disabled = getInventoryTypeDisabled(o);
      return {
        ...o,
        disabled,
      };
    }),
  );
  return {
    list,
  };
}
export function useInventoryTipsPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  ios14QuotaInfo?: Ref<Ios14Quota>;
  isEdit: Ref<boolean>;
}) {
  const { adgroupData, campaignData, ios14QuotaInfo, isEdit } = props;
  const { isOnlyTiktok, isOnlyPangle, isOnlySupportTT } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isDedicateCampaign } = useAdgroupIos14({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const isCampaignOccupancyQuota = computed(
    () =>
      ios14QuotaInfo?.value?.campaignQuota?.isCampaignOccupancyPangleQuota ||
      ios14QuotaInfo?.value?.campaignQuota?.isCampaignOccupancyTiktokQuota,
  );

  const visible = computed(
    () =>
      ios14QuotaInfo.value.isAppIdInWhitelist &&
      isDedicateCampaign.value &&
      !isOnlySupportTT.value &&
      !isEdit.value &&
      !isCampaignOccupancyQuota?.value &&
      (isOnlyTiktok.value || isOnlyPangle.value),
  );
  const placementTitle = computed(() =>
    isOnlyTiktok.value ? i18n.t('placement_tiktok') : i18n.t('placement_tiktok_ad_network_view'),
  );
  const tipsContent = computed(() =>
    i18n.t('adgroup_placement_avaliable_flexible_one_reminder', { name: placementTitle.value }),
  );
  return {
    visible,
    tipsContent,
  };
}
export function useSearchDeliveryTypeSwitchPropsVisible(props: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = props;
  const { isLeadAds, isTraffic, isConversion, isReach, isVideoView, isAppInstall } = useStdObjectiveType({
    campaignFormData: campaignData,
  });
  const { isSearchAdsWhite, isLeadAdsWhite } = useAllowlist();

  const objectiveIsSupportSearchAds = computed(
    () =>
      isTraffic.value ||
      isAppInstall.value ||
      (isLeadAds.value && isLeadAdsWhite.value) ||
      isConversion.value ||
      isReach.value ||
      isVideoView.value,
  );

  return {
    visible: computed(() => isSearchAdsWhite.value && objectiveIsSupportSearchAds.value),
  };
}

export const useCatalogSupportPangle = (props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<CreateMode>;
}) => {
  const { adgroupData, campaignData, createMode } = props;
  const { getStore } = useStore();
  // 直接请求接口
  const { data } = getStore(NetStateEnum.pangleCatalogCountryList);
  const { isStandardAdsWithCatalog, isProductSales, isOnlyTiktok } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { catalogCountryId: currentCountries } = useCatalogStore({ adgroupFormData: adgroupData });

  const pangleCatalogCountryIDList = computed(() => (data.value || []).map(item => item.id));
  const isCurrentCountriesSupportPangle = computed(() => {
    if (isProductSales.value && isStandardAdsWithCatalog.value && currentCountries.value.length > 0) {
      return currentCountries.value.every(item => pangleCatalogCountryIDList.value.includes(item));
    }
    // 默认是所有国家支持，保持线上逻辑
    return true;
  });
  // 当只选择了Tiktok placement，并且当前国家不支持pangle，则展示TT only的UI
  const isSelectedOnlyTiktokAndNotSupportPangle = computed(
    () =>
      adgroupData.value.inventoryFlowType === FlowType.SELECTED &&
      isOnlyTiktok.value &&
      !isCurrentCountriesSupportPangle.value,
  );

  return {
    pangleCatalogCountryIDList,
    isSelectedOnlyTiktokAndNotSupportPangle,
    isCurrentCountriesSupportPangle,
  };
};

export function useTiktokItemPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, createMode } = props;
  const { isOnlySupportTT } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isSelectedOnlyTiktokAndNotSupportPangle } = useCatalogSupportPangle({
    campaignData,
    adgroupData,
    createMode,
  });

  // isOnlySupportTT 为false时，还需要根据当前国家判断是否支持Pangle
  const visible = computed(() => isOnlySupportTT.value || isSelectedOnlyTiktokAndNotSupportPangle.value);

  return {
    visible,
  };
}

export function useSearchDeliveryTypeSwitchPropsDisabled(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  createMode: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, adgroupConfig, createMode } = props;
  const { isHasTikTok } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { visible: isOnlySupportTT } = useTiktokItemPropsVisible({
    adgroupData,
    campaignData,
    createMode,
  });

  return {
    disabled: computed(() =>
      !isOnlySupportTT.value
        ? adgroupConfig.value.disablePlacement || !isHasTikTok.value
        : adgroupConfig.value.disableSearchAdsOnlyTT,
    ),
  };
}

export function usePlacementOnlyTikTokTips(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isPSA, isLiveAds, isStandardAdsWithShop, isStandardAdsWithShowcase } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const tips = computed(() => {
    if (isPSA.value) {
      return {
        tipsI18nText: 'module_placement_tt_psa_desc',
        tooltipInfo: {
          linkHref: '/help/article/about-onsite-shopping-placements',
          linkI18nText: 'common_learn_more',
          tipsI18n: 'module_placement_tt_psa_tips_desc',
        },
      };
    }
    if (isLiveAds.value || isStandardAdsWithShop.value || isStandardAdsWithShowcase.value) {
      return {
        tipsI18nText: 'module_placement_tt_desc',
        tooltipInfo: {
          linkHref: '/help/article/about-onsite-shopping-placements',
          linkI18nText: 'common_learn_more',
          tipsI18n: 'module_placement_tt_hover_hint',
        },
      };
    }
    return null;
  });
  return {
    tips,
  };
}

// export function useLabelHintPropsVisible(props: { campaignData: Ref<CampaignFormData> }) {
//   const { campaignData } = props;
//   const { isAmazonAppinstall } = useStdAdgroupAmazon({
//     campaignFormData: campaignData,
//   });
//   const { isBrandAuction } = useStdObjectiveType({
//     campaignFormData: campaignData,
//   });
//   return {
//     visible: computed(() => !isAmazonAppinstall.value && !isBrandAuction.value),
//   };
// }

// eslint-disable-next-line max-lines-per-function
export function useInventoryListPropsList(props: {
  adgroupData: Ref<AdgroupFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  campaignData: Ref<CampaignFormData>;
  ios14QuotaInfo?: Ref<Ios14Quota>;
  createMode?: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, adgroupConfig, ios14QuotaInfo, createMode } = props;
  const { isAppTypeIOS, isHasTikTok, isTiktokWebPage, isSupportPangle } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isEdit } = useAdgroupMode({ createMode });
  const { isDpa, isProductSales } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { isAmazonTraffic } = useStdAdgroupAmazon({
    campaignFormData: campaignData,
  });
  const { isSearchAdsWhite, isSearchAdsPreviewWhite } = useAllowlist();

  const { visible: isOnlySupportTT } = useTiktokItemPropsVisible({
    campaignData,
    adgroupData,
    createMode,
  });
  const { isDedicateCampaign } = useAdgroupIos14({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const fullInventoryFlows = computed(() => [
    {
      img: PlacementTTImage,
      id: InventoryFlow.TIKTOK,
      title: i18n.t('placement_tiktok'),
      // desc: i18n.t('placement_tiktok_info'),
      previewList: [
        {
          title: `${i18n.t('placement_tiktok', { X: '' })}1`,
          img: PlacementTTViewImage,
        },
        {
          title: `${i18n.t('placement_tiktok', { X: '' })}2`,
          img: PlacementTTSearchAdsImage,
          hide: !(
            isSearchAdsWhite.value &&
            isSearchAdsPreviewWhite.value &&
            adgroupData.value.searchDeliveryType === SearchAdsSwitch.OPEN
          ),
        },
      ],
      quotaDisabled: false,
    },
    {
      img: PlacementTBImage,
      id: InventoryFlow.TOPBUZZ,
      title: i18n.t('placement_news_feed_app'),
      desc: i18n.t('placement_news_feed_app_info'),
      previewList: [
        {
          title: `${i18n.t('placement_news_feed_view', { X: '' })}1`,
          img: PlacementTB1Image,
        },
        {
          title: `${i18n.t('placement_news_feed_view', { X: '' })}2`,
          img: PlacementTB2Image,
        },
        {
          title: `${i18n.t('placement_news_feed_view', { X: '' })}3`,
          img: PlacementTB3Image,
        },
      ],
      quotaDisabled: false,
    },
    // 临时注释，之后补充了编辑逻辑之后把相关逻辑补充上去
    // {
    //   img: '//sf16-sg.tiktokcdn.com/obj/eden-sg/nz_zkh_rvarpa/ljhwZthlaukjlkulzlp/ttam/images/placement/placement-helo.png',
    //   id: InventoryFlow.HELO,
    //   title: i18n.t('placement_helo_view'),
    //   desc: i18n.t('placement_helo_info'),
    //   previewList: [
    //     {
    //       title: `${i18n.t('placement_helo_view') } 1`,
    //       img: '//sf16-sg.tiktokcdn.com/obj/eden-sg/nz_zkh_rvarpa/ljhwZthlaukjlkulzlp/ttam/images/placement/placement-helo-view1.png',
    //     },
    //     {
    //       title: `${i18n.t('placement_helo_view') } 2`,
    //       img: '//sf16-sg.tiktokcdn.com/obj/eden-sg/nz_zkh_rvarpa/ljhwZthlaukjlkulzlp/ttam/images/placement/placement-helo-view2.png',
    //     },
    //   ],
    // },
    {
      img: PlacementPanpleImage,
      id: InventoryFlow.TIKTOKADNETWORK,
      title: i18n.t('placement_tiktok_ad_network_view'),
      desc: i18n.t('placement_tiktok_ad_network_info'),
      more: true,
      previewList: [
        {
          title: i18n.t('placement_tiktok_ad_network_view'),
          img: PlacementAdNetworkImage,
        },
      ],
      quotaDisabled: false,
    },
  ]);
  // 获取版位禁用提示
  const getDisabledTooltipContent = (
    disabled: boolean,
    isCampaignOccupancyQuota: boolean,
    placementTitle: string,
    id: number,
  ) => {
    if (!disabled) {
      return '';
    }
    const isPanglePlacement = InventoryFlow.TIKTOKADNETWORK === id;

    const limitInventoryFlowTitle = isPanglePlacement
      ? i18n.t('placement_tiktok')
      : i18n.t('placement_tiktok_ad_network_view');
    if (isCampaignOccupancyQuota) {
      return i18n.t('adgroup_placement_avaliable_flexible_one_reminder', { name: limitInventoryFlowTitle }) as string;
    }
    return i18n.t('adgroup_placement_no_quota_one_flexible_tips', { name: placementTitle }) as string;
  };
  // 判断ios14场景下版位禁用
  const handleIos14PlacementDisabled = item => {
    let disabled = false;
    const isPlacementOccupancyTiktokQuota = [InventoryFlow.TIKTOK].includes(item.id);

    const isPanglePlacement = item.id === InventoryFlow.TIKTOKADNETWORK;

    const isTiktokQuotaCanNotUse =
      isPlacementOccupancyTiktokQuota && ios14QuotaInfo?.value.campaignQuota.tikTokQuotaAvailable <= 0;
    const isPangleQuotaCanNotUse = isPanglePlacement && ios14QuotaInfo?.value.campaignQuota.pangleQuota <= 0;
    const isPlacementNotSupport = !ios14QuotaInfo?.value.adgroupQuota.inventoryFlow.includes(item.id);

    const isCampaignOccupancyQuota =
      ios14QuotaInfo?.value?.campaignQuota?.isCampaignOccupancyPangleQuota ||
      ios14QuotaInfo?.value?.campaignQuota?.isCampaignOccupancyTiktokQuota;

    if (isPanglePlacement) {
      disabled = isCampaignOccupancyQuota ? isPlacementNotSupport : isPangleQuotaCanNotUse;
    } else if (isPlacementOccupancyTiktokQuota) {
      disabled = isCampaignOccupancyQuota ? isPlacementNotSupport : isTiktokQuotaCanNotUse;
    }
    const disabledTooltipContent = getDisabledTooltipContent(disabled, isCampaignOccupancyQuota, item.title, item.id);
    return {
      disabled: item.disabled || disabled,
      quotaDisabled: disabled,
      disabledTooltipContent,
    };
  };
  // product sales版位过滤逻辑
  const handleFilterProductSalesInventoryFlows = item => {
    if (isSupportPangle.value) {
      return [InventoryFlow.TIKTOK, InventoryFlow.TIKTOKADNETWORK].includes(item.id);
    }
    return [InventoryFlow.TIKTOK].includes(item.id);
  };
  const list = computed(() =>
    fullInventoryFlows.value
      .filter(item => {
        if (isAmazonTraffic.value) {
          return item.id !== InventoryFlow.TIKTOKADNETWORK;
        }
        if (isDpa.value && !isOnlySupportTT.value) {
          return [InventoryFlow.TIKTOK, InventoryFlow.TIKTOKADNETWORK].includes(item.id);
        }
        if (isSupportPangle.value) {
          return [InventoryFlow.TIKTOK, InventoryFlow.TIKTOKADNETWORK].includes(item.id);
        }
        if (isProductSales.value) {
          return handleFilterProductSalesInventoryFlows(item);
        }
        return item;
      })
      .map(i => {
        let disabledConfig = {
          disabled: false,
          quotaDisabled: false,
          disabledTooltipContent: '',
        };

        const isAppIdInWhitelist = isDedicateCampaign.value && ios14QuotaInfo?.value.isAppIdInWhitelist;

        if (i.id === InventoryFlow.HELO) {
          disabledConfig.disabled = isAppTypeIOS.value;
        }

        // instant page tiktok 不能取消
        if (i.id === InventoryFlow.TIKTOK) {
          disabledConfig.disabled =
            (isHasTikTok.value && isTiktokWebPage.value) ||
            adgroupData.value?.adAppProfilePageType === AppProfilePageType.on;
        }
        if (isAppIdInWhitelist && !isEdit.value) {
          const ios14DisabledConfig = handleIos14PlacementDisabled(i);
          disabledConfig = ios14DisabledConfig;
        }
        disabledConfig.disabled = disabledConfig.disabled || adgroupConfig.value.disablePlacement;
        return {
          ...i,
          ...disabledConfig, // vigo已下线
        };
      }),
  );
  const hasDisabledPlacement = computed(() => list.value.some(o => o.quotaDisabled));
  return {
    list,
    hasDisabledPlacement,
  };
}

// export function usePropsShowTips(props: {
//   adgroupData: Ref<AdgroupFormData>;
//   adgroupConfig: Ref<AdgroupConfig>;
//   campaignData: Ref<CampaignFormData>;
//   inventoryFlows: Ref<InventoryFlowListItem[]>;
// }) {
//   const { adgroupData, campaignData, inventoryFlows, adgroupConfig } = props;
//   const { isAppTypeIOS } = useStdAdgroupIs({
//     adgroupFormData: adgroupData,
//     campaignFormData: campaignData,
//   });

//   const isManualSelect = computed(() => adgroupData.value.inventoryFlowType === FlowType.SELECTED);
//   const isLimitedOPlacementsExist = computed(() => {
//     if (adgroupConfig.value.disablePlacement) {
//       return true;
//     }

//     if (!isAppTypeIOS.value) {
//       return false;
//     }

//     return inventoryFlows.value.some(item => item.id === InventoryFlow.HELO || item.id === InventoryFlow.VIGO);
//   });
//   return {
//     showTips: computed(() => isManualSelect.value && isLimitedOPlacementsExist.value),
//   };
// }

/**
 * 版位相关生成
 * @param objective Objective Type
 */
export function useInitDataGet(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { campaignData, adgroupData } = props;

  const { visible: isSearchAdsSupported } = useSearchDeliveryTypeSwitchPropsVisible({
    campaignData,
  });

  const { isOnlySupportTT } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const { list } = useInventoryListPropsList({
    adgroupConfig: ref({} as any),
    adgroupData,
    campaignData,
  });

  const searchDeliveryType = isSearchAdsSupported.value ? SearchAdsSwitch.OPEN : SearchAdsSwitch.CLOSE;

  const inventoryFlowType = isOnlySupportTT.value ? FlowType.SELECTED : FlowType.AUTO;

  const inventoryFlow = isOnlySupportTT.value ? [InventoryFlow.TIKTOK] : list.value.map(o => o.id);

  return {
    searchDeliveryType,
    inventoryFlow,
    inventoryFlowType,
  };
}
